import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Users, Heart, Globe, Clock, MapPin, User, ArrowRight, Sparkles, Search, X } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { ministries as defaultMinistries } from '../../data/seedData'
import { addDocument } from '../../services/firebase'

const categoryIcons = {
  men: Users,
  women: Heart,
  youth: Users,
  campus: Users,
  groups: Users,
  outreach: Globe,
}

const categoryColors = {
  men: 'from-blue-500 to-indigo-500',
  women: 'from-rose-500 to-pink-500',
  youth: 'from-secondary-500 to-purple-500',
  campus: 'from-accent-500 to-orange-500',
  groups: 'from-primary-500 to-blue-500',
  outreach: 'from-emerald-500 to-teal-500',
}

const categories = [
  { id: 'all', label: 'All Ministries' },
  { id: 'men', label: 'Men' },
  { id: 'women', label: 'Women' },
  { id: 'youth', label: 'Young Adults' },
  { id: 'campus', label: 'Campus' },
  { id: 'groups', label: 'Small Groups' },
  { id: 'outreach', label: 'Outreach' },
]

const MinistryCard = ({ ministry, index, onGetInvolved }) => {
  const Icon = categoryIcons[ministry.category] || Heart
  const gradient = categoryColors[ministry.category] || 'from-primary-500 to-secondary-500'

  return (
    <motion.div
      className="group bg-white rounded-3xl shadow-soft hover:shadow-soft-lg overflow-hidden transition-all"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -8 }}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={ministry.imageUrl || 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600'}
          alt={ministry.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-dark/20 to-transparent" />

        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r ${gradient} text-white text-xs font-bold shadow-lg`}>
            <Icon className="w-3 h-3" />
            {ministry.category}
          </span>
        </div>

        {/* Icon Box */}
        <motion.div
          className={`absolute bottom-4 right-4 w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg`}
          whileHover={{ scale: 1.1, rotate: 5 }}
        >
          <Icon className={`w-6 h-6 bg-gradient-to-r ${gradient} bg-clip-text`} style={{ color: 'transparent', backgroundClip: 'text', WebkitBackgroundClip: 'text', backgroundImage: `linear-gradient(to right, var(--tw-gradient-stops))` }} />
        </motion.div>
      </div>

      <div className="p-6">
        <h3 className="font-heading font-bold text-xl text-dark-100 mb-3 group-hover:text-primary-600 transition-colors">
          {ministry.name}
        </h3>

        <p className="text-dark-300 mb-4 line-clamp-2 leading-relaxed">{ministry.description}</p>

        <div className="space-y-2 mb-5">
          {ministry.leaderName && (
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <div className="p-1.5 rounded-lg bg-primary-50">
                <User className="w-3.5 h-3.5 text-primary-500" />
              </div>
              <span>{ministry.leaderName}</span>
            </div>
          )}
          {ministry.schedule && (
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <div className="p-1.5 rounded-lg bg-secondary-50">
                <Clock className="w-3.5 h-3.5 text-secondary-500" />
              </div>
              <span>{ministry.schedule}</span>
            </div>
          )}
          {ministry.location && (
            <div className="flex items-center gap-2 text-sm text-dark-300">
              <div className="p-1.5 rounded-lg bg-accent-50">
                <MapPin className="w-3.5 h-3.5 text-accent-500" />
              </div>
              <span>{ministry.location}</span>
            </div>
          )}
        </div>

        <motion.button
          onClick={() => onGetInvolved(ministry)}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-primary-500 text-primary-600 font-semibold hover:bg-primary-500 hover:text-white transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Get Involved
          <ArrowRight className="w-4 h-4" />
        </motion.button>
      </div>
    </motion.div>
  )
}

const Ministries = () => {
  const [activeCategory, setActiveCategory] = useState('all')
  const [showJoinGroupModal, setShowJoinGroupModal] = useState(false)
  const [showVolunteerModal, setShowVolunteerModal] = useState(false)
  const [selectedMinistry, setSelectedMinistry] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const [joinGroupForm, setJoinGroupForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDay: '',
    message: ''
  })

  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    phone: '',
    ministry: '',
    experience: '',
    availability: '',
    message: ''
  })

  const { data: ministries } = useCollection('ministries', {
    orderByField: 'name',
    orderDirection: 'asc',
  })

  const displayMinistries = ministries?.length > 0
    ? ministries.filter(m => m.isActive)
    : defaultMinistries

  const filteredMinistries = activeCategory === 'all'
    ? displayMinistries
    : displayMinistries.filter(m => m.category === activeCategory)

  const handleGetInvolved = (ministry) => {
    setSelectedMinistry(ministry)
    if (ministry.category === 'groups') {
      setShowJoinGroupModal(true)
    } else {
      setVolunteerForm(prev => ({ ...prev, ministry: ministry.name }))
      setShowVolunteerModal(true)
    }
  }

  const handleJoinGroupSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await addDocument('formSubmissions', {
        ...joinGroupForm,
        formType: 'joinGroup',
        ministry: selectedMinistry?.name || 'Small Groups',
        submittedAt: new Date().toISOString(),
        status: 'new'
      })
      setSubmitSuccess(true)
      setJoinGroupForm({ name: '', email: '', phone: '', preferredDay: '', message: '' })
      setTimeout(() => {
        setShowJoinGroupModal(false)
        setSubmitSuccess(false)
      }, 2000)
    } catch (error) {
      console.error('Error submitting form:', error)
    }
    setIsSubmitting(false)
  }

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await addDocument('formSubmissions', {
        ...volunteerForm,
        formType: 'volunteer',
        submittedAt: new Date().toISOString(),
        status: 'new'
      })
      setSubmitSuccess(true)
      setVolunteerForm({ name: '', email: '', phone: '', ministry: '', experience: '', availability: '', message: '' })
      setTimeout(() => {
        setShowVolunteerModal(false)
        setSubmitSuccess(false)
      }, 2000)
    } catch (error) {
      console.error('Error submitting form:', error)
    }
    setIsSubmitting(false)
  }

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
              whileHover={{ scale: 1.05 }}
            >
              <Heart className="w-4 h-4 text-secondary-400" fill="currentColor" />
              Get Involved
            </motion.span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6">
              Our <span className="bg-gradient-to-r from-secondary-400 to-accent-400 bg-clip-text text-transparent">Ministries</span>
            </h1>
            <p className="text-xl text-white/70">
              Discover opportunities to grow, serve, and connect with others through our diverse ministry programs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 bg-white border-b sticky top-20 z-20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-5 py-2.5 rounded-full font-semibold whitespace-nowrap transition-all ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                    : 'bg-light-100 text-dark-300 hover:bg-light-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.label}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Ministries Grid */}
      <section className="py-24 bg-gradient-to-b from-white to-light-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {filteredMinistries.map((ministry, index) => (
                <MinistryCard key={ministry.id} ministry={ministry} index={index} onGetInvolved={handleGetInvolved} />
              ))}
            </motion.div>
          </AnimatePresence>

          {filteredMinistries.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-light-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-dark-300" />
              </div>
              <p className="text-dark-300 text-lg">No ministries found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/30"
              whileHover={{ scale: 1.05 }}
            >
              <Sparkles className="w-4 h-4" />
              Make a Difference
            </motion.span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
              Ready to Serve?
            </h2>
            <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
              Every member has a role to play in God's kingdom work. Discover your gifts and find your place to serve.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <motion.button
                onClick={() => {
                  setVolunteerForm(prev => ({ ...prev, ministry: '' }))
                  setShowVolunteerModal(true)
                }}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white text-primary-600 font-bold shadow-xl"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Heart className="w-5 h-5" />
                Volunteer Application
              </motion.button>
              <motion.button
                onClick={() => window.location.href = '/contact'}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-white text-white font-bold hover:bg-white/10 transition-colors"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Join Group Modal */}
      <AnimatePresence>
        {showJoinGroupModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowJoinGroupModal(false)}
          >
            <motion.div
              className="bg-white rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-heading font-bold text-dark-100">Join a Small Group</h3>
                <button
                  onClick={() => setShowJoinGroupModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {submitSuccess ? (
                <motion.div
                  className="text-center py-8"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-dark-100 mb-2">Thank You!</h4>
                  <p className="text-gray-600">We'll be in touch soon about small group opportunities.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleJoinGroupSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={joinGroupForm.name}
                      onChange={(e) => setJoinGroupForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={joinGroupForm.email}
                      onChange={(e) => setJoinGroupForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={joinGroupForm.phone}
                      onChange={(e) => setJoinGroupForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Meeting Day</label>
                    <select
                      value={joinGroupForm.preferredDay}
                      onChange={(e) => setJoinGroupForm(prev => ({ ...prev, preferredDay: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    >
                      <option value="">Select a day</option>
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      value={joinGroupForm.message}
                      onChange={(e) => setJoinGroupForm(prev => ({ ...prev, message: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all resize-none"
                      placeholder="Tell us about yourself or any questions you have..."
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold shadow-lg disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </motion.button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Volunteer Modal */}
      <AnimatePresence>
        {showVolunteerModal && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVolunteerModal(false)}
          >
            <motion.div
              className="bg-white rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-heading font-bold text-dark-100">Volunteer Application</h3>
                <button
                  onClick={() => setShowVolunteerModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {submitSuccess ? (
                <motion.div
                  className="text-center py-8"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-dark-100 mb-2">Thank You!</h4>
                  <p className="text-gray-600">We appreciate your heart to serve. We'll contact you soon!</p>
                </motion.div>
              ) : (
                <form onSubmit={handleVolunteerSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={volunteerForm.name}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={volunteerForm.email}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      required
                      value={volunteerForm.phone}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all"
                      placeholder="(123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ministry Interest *</label>
                    <select
                      required
                      value={volunteerForm.ministry}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, ministry: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all"
                    >
                      <option value="">Select a ministry</option>
                      {displayMinistries.map(m => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
                    <select
                      value={volunteerForm.availability}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, availability: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all"
                    >
                      <option value="">Select availability</option>
                      <option value="Weekdays">Weekdays</option>
                      <option value="Weekends">Weekends</option>
                      <option value="Both">Both Weekdays & Weekends</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Previous Experience</label>
                    <textarea
                      value={volunteerForm.experience}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, experience: e.target.value }))}
                      rows={2}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all resize-none"
                      placeholder="Any relevant experience..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Message</label>
                    <textarea
                      value={volunteerForm.message}
                      onChange={(e) => setVolunteerForm(prev => ({ ...prev, message: e.target.value }))}
                      rows={2}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none transition-all resize-none"
                      placeholder="Anything else you'd like us to know..."
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-accent-500 to-accent-600 text-white font-semibold shadow-lg disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </motion.button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Ministries
